import { useEffect, useState } from 'react';
import { Button } from '@tark/shared/components';
import {
  useConfirmTenantSessionCharge,
  useMarkTenantSessionChargeNotReceived,
  useRequestTenantSessionCharge,
} from '@tark/shared/hooks';
import type {
  TenantSessionCharge,
  TenantSessionPaymentMethod,
  TenantSessionRosterEntry,
} from '@tark/shared/types';
import { WalletCards } from 'lucide-react';

const METHODS: Array<{ value: TenantSessionPaymentMethod; label: string }> = [
  { value: 'ZELLE', label: 'Zelle' },
  { value: 'VENMO', label: 'Venmo' },
  { value: 'CASH_APP', label: 'Cash App' },
  { value: 'CASH', label: 'Cash' },
  { value: 'CHECK', label: 'Check' },
  { value: 'OTHER_OFF_PLATFORM', label: 'Other' },
];

type EditorMode = 'request' | 'not-received' | null;

export default function SessionPaymentRow({
  occurrenceId,
  entry,
}: {
  occurrenceId: number;
  entry: TenantSessionRosterEntry;
}) {
  const requestCharge = useRequestTenantSessionCharge();
  const confirmCharge = useConfirmTenantSessionCharge();
  const markNotReceived = useMarkTenantSessionChargeNotReceived();
  const [charge, setCharge] = useState<TenantSessionCharge | null>(entry.paymentCharge ?? null);
  const [editorMode, setEditorMode] = useState<EditorMode>(null);
  const [amount, setAmount] = useState(formatAmountInput(entry.paymentCharge?.amount ?? entry.priceAmount));
  const [method, setMethod] = useState<TenantSessionPaymentMethod>(entry.paymentCharge?.methodHint ?? 'ZELLE');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setCharge(entry.paymentCharge ?? null);
    setAmount(formatAmountInput(entry.paymentCharge?.amount ?? entry.priceAmount));
    setMethod(entry.paymentCharge?.methodHint ?? 'ZELLE');
  }, [entry.paymentCharge, entry.priceAmount]);

  const paymentStatus = (entry.paymentStatus || 'PENDING').toUpperCase();
  const coveredWithoutCharge = !charge && ['PAID', 'PACKAGE', 'WAIVED', 'REFUNDED'].includes(paymentStatus);
  if ((entry.bookingStatus || '').toUpperCase() === 'CANCELLED' || coveredWithoutCharge) return null;

  const busy = requestCharge.isPending || confirmCharge.isPending || markNotReceived.isPending;
  const canRequest = !charge || charge.canEditRequest;
  const amountLabel = formatMoney(charge?.amount ?? entry.priceAmount, charge?.currencyCode ?? entry.currencyCode ?? 'USD');

  const openRequest = () => {
    setAmount(formatAmountInput(charge?.amount ?? entry.priceAmount));
    setMethod(charge?.methodHint ?? 'ZELLE');
    setError(null);
    setEditorMode('request');
  };

  const saveRequest = async () => {
    const parsedAmount = Number(amount);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      setError('Enter an amount greater than zero.');
      return;
    }
    setError(null);
    try {
      const updated = await requestCharge.mutateAsync({
        bookingId: entry.bookingId,
        occurrenceId,
        athleteId: entry.athleteId,
        payload: { amount: parsedAmount, currencyCode: charge?.currencyCode ?? entry.currencyCode ?? 'USD', methodHint: method },
      });
      setCharge(updated);
      setEditorMode(null);
    } catch {
      setError('Payment request was not saved. Please try again.');
    }
  };

  const confirmReceived = async () => {
    if (!charge || !window.confirm(`Confirm ${amountLabel} was received? This will mark the booking paid.`)) return;
    setError(null);
    try {
      setCharge(await confirmCharge.mutateAsync({ bookingId: entry.bookingId, chargeId: charge.chargeId, occurrenceId, athleteId: entry.athleteId }));
    } catch {
      setError('Payment was not confirmed. Please try again.');
    }
  };

  const saveNotReceived = async () => {
    if (!charge || !note.trim()) {
      setError('Add a short note for the family.');
      return;
    }
    setError(null);
    try {
      setCharge(await markNotReceived.mutateAsync({
        bookingId: entry.bookingId,
        chargeId: charge.chargeId,
        occurrenceId,
        athleteId: entry.athleteId,
        payload: { note: note.trim() },
      }));
      setNote('');
      setEditorMode(null);
    } catch {
      setError('Payment status was not updated. Please try again.');
    }
  };

  return (
    <div className="mt-3 border-t border-border pt-3" data-testid={`session-payment-${entry.bookingId}`}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <WalletCards className="h-4 w-4" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-text">Payment · {amountLabel}</p>
            <p className="text-xs text-text-muted">{statusCopy(charge)}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canRequest ? <Button size="xs" variant="outline" disabled={busy} onClick={openRequest}>{charge ? 'Edit' : 'Request'}</Button> : null}
          {charge?.status === 'SENT' && charge.canConfirm ? (
            <>
              <Button size="xs" variant="success" isLoading={confirmCharge.isPending} onClick={() => void confirmReceived()}>Confirm</Button>
              <Button size="xs" variant="ghost" disabled={busy} className="text-red-600" onClick={() => { setError(null); setNote(''); setEditorMode('not-received'); }}>Not received</Button>
            </>
          ) : null}
        </div>
      </div>

      {editorMode === 'request' ? (
        <div className="mt-3 grid gap-3 rounded-lg border border-border bg-surface-elevated/30 p-3 sm:grid-cols-[minmax(0,0.7fr)_minmax(0,1fr)_auto] sm:items-end">
          <label className="text-xs font-semibold text-text-muted">Amount
            <input aria-label="Payment amount" type="number" min="0.01" step="0.01" value={amount} onChange={(event) => setAmount(event.target.value)} className="mt-1 h-9 w-full rounded-lg border border-border bg-surface px-3 text-sm text-text outline-none focus:border-primary" />
          </label>
          <label className="text-xs font-semibold text-text-muted">Payment method
            <select aria-label="Payment method" value={method} onChange={(event) => setMethod(event.target.value as TenantSessionPaymentMethod)} className="mt-1 h-9 w-full rounded-lg border border-border bg-surface px-3 text-sm text-text outline-none focus:border-primary">
              {METHODS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </label>
          <div className="flex gap-2">
            <Button size="sm" isLoading={requestCharge.isPending} onClick={() => void saveRequest()}>{charge ? 'Save' : 'Send request'}</Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => setEditorMode(null)}>Cancel</Button>
          </div>
          <p className="text-xs text-text-subtle sm:col-span-3">TARK records the status only. Payment happens outside TARK.</p>
        </div>
      ) : null}

      {editorMode === 'not-received' ? (
        <div className="mt-3 flex flex-col gap-3 rounded-lg border border-border bg-surface-elevated/30 p-3 sm:flex-row sm:items-end">
          <label className="min-w-0 flex-1 text-xs font-semibold text-text-muted">Short note
            <input aria-label="Payment not received note" value={note} maxLength={240} placeholder="Example: Please check the transfer" onChange={(event) => setNote(event.target.value)} className="mt-1 h-9 w-full rounded-lg border border-border bg-surface px-3 text-sm text-text outline-none focus:border-primary" />
          </label>
          <Button size="sm" variant="danger" isLoading={markNotReceived.isPending} onClick={() => void saveNotReceived()}>Return to requested</Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => setEditorMode(null)}>Cancel</Button>
        </div>
      ) : null}

      {error ? <p role="alert" className="mt-2 text-xs font-medium text-red-600">{error}</p> : null}
    </div>
  );
}

function statusCopy(charge: TenantSessionCharge | null): string {
  if (!charge) return 'No request sent';
  if (charge.status === 'SENT') return `Sent${charge.sentMethod ? ` via ${formatMethod(charge.sentMethod)}` : ''} · confirm receipt`;
  if (charge.status === 'RECEIVED') return 'Paid · confirmed received';
  if (charge.status === 'CANCELLED') return 'Closed';
  return charge.notReceivedNote ? 'Not received · family can mark sent again' : 'Requested · waiting for family';
}

function formatMethod(value: string): string {
  return value.toLowerCase().split('_').map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
}

function formatAmountInput(value?: number | null): string {
  return typeof value === 'number' && Number.isFinite(value) ? value.toFixed(2) : '';
}

function formatMoney(value?: number | null, currency = 'USD'): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 'Amount needed';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(value);
  } catch {
    return `$${value.toFixed(2)}`;
  }
}
